import React, { useMemo } from 'react';
import {
  ViewMode,
  Period,
  getWeeksInYear,
  getWeekStartEnd,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getLastCompletedWeek,
  formatDisplayDate,
  parseDate,
} from '../lib/dateUtils';

interface PeriodSelectorProps {
  viewMode: ViewMode;
  period: Period;
  onViewModeChange: (mode: ViewMode) => void;
  onPeriodChange: (period: Period) => void;
}

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const YEARS = [2025, 2026, 2027];

export function PeriodSelector({
  viewMode,
  period,
  onViewModeChange,
  onPeriodChange,
}: PeriodSelectorProps) {
  const [startYear, startMonth, startWeek] = useMemo(() => {
    // parseDate interpreta la fecha en hora LOCAL; con new Date(str) se parsea
    // como UTC y en Chile (UTC-4) retrocede un día → desfasaba la semana mostrada.
    const start = parseDate(period.startDate);
    const iso = getISOWeek(start);
    return [start.getFullYear(), start.getMonth() + 1, iso.week];
  }, [period]);

  const weeks = useMemo(() => getWeeksInYear(startYear), [startYear]);

  const handleMonthYearChange = (year: number, month: number) => {
    onPeriodChange({
      startDate: getFirstDayOfMonth(year, month),
      endDate: getLastDayOfMonth(year, month),
      label: `${MONTHS.find(m => m.value === month)?.label} ${year}`,
    });
  };

  const handleWeekChange = (year: number, week: number) => {
    const { startDate, endDate } = getWeekStartEnd(year, week);
    onPeriodChange({
      startDate,
      endDate,
      label: `Semana ${week} de ${year}`,
    });
  };

  const handleLastCompletedWeek = () => {
    const lastWeek = getLastCompletedWeek();
    onViewModeChange('week');
    onPeriodChange(lastWeek);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => onViewModeChange('month')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'month'
                  ? 'bg-white text-slate-900 shadow'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Mensual
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange('week')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'week'
                  ? 'bg-white text-slate-900 shadow'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Semanal
            </button>
          </div>

          <button
            type="button"
            onClick={handleLastCompletedWeek}
            className="px-4 py-1.5 text-sm font-medium text-fycom-blue hover:text-blue-700 transition"
          >
            Ir a última semana
          </button>
        </div>

        <div className="flex items-center gap-3">
          {viewMode === 'month' ? (
            <>
              <select
                value={startYear}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value, 10), startMonth)}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white"
              >
                {YEARS.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
              <select
                value={startMonth}
                onChange={(e) => handleMonthYearChange(startYear, parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white"
              >
                {MONTHS.map(month => <option key={month.value} value={month.value}>{month.label}</option>)}
              </select>
            </>
          ) : (
            <>
              <select
                value={startYear}
                onChange={(e) => {
                  const year = parseInt(e.target.value, 10);
                  const newWeeks = getWeeksInYear(year);
                  const week = newWeeks.find(w => w.week === startWeek) ? startWeek : 1;
                  handleWeekChange(year, week);
                }}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white"
              >
                {YEARS.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
              <select
                value={startWeek}
                onChange={(e) => handleWeekChange(startYear, parseInt(e.target.value, 10))}
                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue outline-none bg-white min-w-[280px]"
              >
                {weeks.map(week => (
                  <option key={week.week} value={week.week}>{week.label}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      <div className="text-sm text-slate-600">
        Período: <span className="font-medium">{formatDisplayDate(period.startDate)} - {formatDisplayDate(period.endDate)}</span>
      </div>
    </div>
  );
}

function getISOWeek(date: Date): { year: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}
